import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import LoadingScreen from "@/components/layout/LoadingScreen";
import PwaManifest from "@/components/layout/PwaManifest";
import Login from "@/pages/Login";
import DriverLogin from "@/pages/DriverLogin";
import Landing from "@/pages/Landing";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import DashboardLayout from "@/pages/DashboardLayout";
import DriverDashboardLayout from "@/pages/DriverDashboardLayout";
import CreateJob from "@/pages/CreateJob";
import Dashboard from "@/pages/Dashboard";
import ClientDashboard from "@/pages/ClientDashboard";
import Invoicing from "@/pages/Invoicing";
import ClientInvoicing from "@/pages/ClientInvoicing";
import InvoiceEdit from "@/pages/InvoiceEdit";
import Financials from "@/pages/Financials";
import JobStatus from "@/pages/JobStatus";
import DriverRuns from "@/pages/DriverRuns";
import Settings from "@/pages/Settings";
import Addresses from "@/pages/Addresses";
import Approvals from "@/pages/Approvals";
import Team from "@/pages/Team";
import ClientJobs from "@/pages/ClientJobs";
import Messages from "@/pages/Messages";
import DriverLogs from "@/pages/DriverLogs";
import DriverRouteMap from "@/pages/DriverRouteMap";
import DriverTracking from "@/pages/DriverTracking";
import PublicTrack from "@/pages/PublicTrack";
import ExternalDriverView from "@/pages/ExternalDriverView";
import "@/App.css";

// Minimum time the cinematic preloader stays on screen before it can exit.
const PRELOADER_MIN_MS = 1500;
// Safety cap — never hold the preloader past this, even if auth stalls.
const PRELOADER_MAX_MS = 4000;

function LoadingGate({ children }) {
  const { loading } = useAuth();
  const startRef = useRef(Date.now());
  // phases: "show" → "exiting" (boom + fade) → "done"
  const [phase, setPhase] = useState("show");

  // Begin the exit once the minimum display time has passed AND auth has
  // resolved — but no later than the safety cap so it can never hang.
  useEffect(() => {
    if (phase !== "show") return;
    const elapsed = Date.now() - startRef.current;
    const target = loading ? PRELOADER_MAX_MS : PRELOADER_MIN_MS;
    const t = setTimeout(() => setPhase("exiting"), Math.max(0, target - elapsed));
    return () => clearTimeout(t);
  }, [loading, phase]);

  if (phase === "done") return children;

  // Mount the page as soon as auth resolves (it stays hidden behind the
  // opaque preloader) so heavy assets like the hero video are already
  // decoded before the boom — no frame hitch when it reveals.
  const mountPage = !loading || phase === "exiting";

  return (
    <>
      {mountPage && children}
      <LoadingScreen
        exiting={phase === "exiting"}
        onExited={() => setPhase("done")}
      />
    </>
  );
}

function DashboardIndex() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "admin") return <Navigate to="/dashboard/home" replace />;
  // Allocators have no Dashboard/home view (financials live there) — land on
  // Job Status, the first of their three permitted areas.
  if (user.role === "client" || user.role === "allocator") return <Navigate to="/dashboard/job-status" replace />;
  // Drivers get their own portal
  return <Navigate to="/driver-dashboard/runs" replace />;
}

// Job Status / History resolve to the client's purpose-built view (which
// hides all admin/pricing info) for clients, and the full admin view for admins.
function JobStatusRoute() {
  const { user } = useAuth();
  return user?.role === "client" ? <ClientJobs /> : <JobStatus />;
}
function JobHistoryRoute() {
  const { user } = useAuth();
  return user?.role === "client" ? <ClientJobs historyMode /> : <JobStatus historyMode />;
}
function InvoicingRoute() {
  const { user } = useAuth();
  return user?.role === "client" ? <ClientInvoicing /> : <Invoicing />;
}

function App() {
  useEffect(() => {
    const removeBadge = () => {
      const badge = document.getElementById("emergent-badge");
      if (badge) badge.remove();
    };
    removeBadge();
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: false });
    return () => observer.disconnect();
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider>
      <BrowserRouter>
        <PwaManifest />
        <Toaster theme="dark" position="top-right" richColors />
        <LoadingGate>
          <Routes>
            {/* ── Public ── */}
            <Route path="/" element={<Landing />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/contact" element={<About />} />
            <Route path="/track" element={<PublicTrack />} />
            <Route path="/track/:jobRef" element={<PublicTrack />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/ext/:token" element={<ExternalDriverView />} />

            {/* ── Auth ── */}
            {/* /login is the generic admin+client login linked from the public site.
                /admin-login and /client-login are the dedicated entry points used as
                the start_url of the installable Admin / Client home-screen apps
                (see public/manifest-*.json and lib/pwa.js). */}
            <Route path="/login" element={<Login />} />
            <Route path="/admin-login" element={<Login portal="admin" />} />
            <Route path="/client-login" element={<Login portal="client" />} />
            <Route path="/driver-login" element={<DriverLogin />} />

            {/* ── Admin + Client Dashboard ── */}
            <Route path="/dashboard" element={<DashboardLayout />}>
              <Route index element={<DashboardIndex />} />
              <Route path="home" element={<Dashboard />} />
              <Route path="invoicing" element={<InvoicingRoute />} />
              <Route path="invoicing/:invoiceId/edit" element={<InvoiceEdit />} />
              <Route path="financials" element={<Financials />} />
              <Route path="create-job" element={<CreateJob />} />
              <Route path="approvals" element={<Approvals />} />
              <Route path="job-status" element={<JobStatusRoute />} />
              <Route path="job-history" element={<JobHistoryRoute />} />
              <Route path="addresses" element={<Addresses />} />
              <Route path="team" element={<Team />} />
              <Route path="driver-logs" element={<DriverLogs />} />
              <Route path="driver-tracking" element={<DriverTracking />} />
              <Route path="messages" element={<Messages />} />
              {/* Client */}
              <Route path="my-jobs" element={<Navigate to="/dashboard/job-status" replace />} />
              <Route path="my-dashboard" element={<ClientDashboard />} />
              <Route path="new-request" element={<CreateJob clientMode />} />
              {/* Shared */}
              <Route path="settings" element={<Settings />} />
              {/* Redirect drivers away */}
              <Route path="runs" element={<Navigate to="/driver-dashboard/runs" replace />} />
            </Route>

            {/* ── Driver Dashboard ── */}
            <Route path="/driver-dashboard" element={<DriverDashboardLayout />}>
              <Route index element={<Navigate to="/driver-dashboard/runs" replace />} />
              <Route path="runs" element={<DriverRuns />} />
              <Route path="route" element={<DriverRouteMap />} />
              <Route path="messages" element={<Messages />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </LoadingGate>
      </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
